import { sealPayload, openPayload } from './oem-vault';
import Dexie, { type EntityTable } from 'dexie';

export interface CachedRecord {
  readonly key: string;
  readonly organizationId: string;
  readonly userId: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly version: number;
  readonly payload: unknown;
  readonly syncedAt: string;
}

export interface PendingMutation {
  readonly id: string;
  readonly organizationId: string;
  readonly userId: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly operation: 'create' | 'update' | 'delete';
  readonly baseVersion: number;
  readonly payload: unknown;
  readonly createdAt: string;
  readonly retryCount: number;
  readonly lastError?: string;
  readonly idempotencyKey: string;
}

export interface SyncConflict {
  readonly id: string;
  readonly mutationId: string;
  readonly organizationId: string;
  readonly userId: string;
  readonly entityType: string;
  readonly entityId: string;
  readonly baseVersion: number;
  readonly serverVersion: number;
  readonly localPayload: unknown;
  readonly serverPayload: unknown;
  readonly detectedAt: string;
}

export interface MutationTransportResult {
  readonly state: 'applied' | 'conflict' | 'retry' | 'skip';
  readonly serverVersion?: number;
  readonly serverPayload?: unknown;
  readonly error?: string;
}

export type ConflictResolution = 'keep_server' | 'save_local_as_new_version' | 'manual_merge';

export class SqiOfflineDatabase extends Dexie {
  records!: EntityTable<CachedRecord, 'key'>;
  mutations!: EntityTable<PendingMutation, 'id'>;
  conflicts!: EntityTable<SyncConflict, 'id'>;
  quarantine!: EntityTable<PendingMutation, 'id'>;

  constructor() {
    super('sqi-quote-compliance-offline');
    this.version(1).stores({
      records: '&key,[organizationId+userId],entityType,entityId,syncedAt',
      mutations: '&id,[organizationId+userId],entityType,entityId,createdAt,idempotencyKey',
    });
    this.version(2).stores({
      records: '&key,[organizationId+userId],entityType,entityId,syncedAt',
      mutations: '&id,[organizationId+userId],entityType,entityId,createdAt,&idempotencyKey',
      conflicts: '&id,&mutationId,[organizationId+userId],entityType,entityId,detectedAt',
    });
    this.version(3)
      .stores({
        records: '&key,[organizationId+userId],entityType,entityId,syncedAt',
        mutations: '&id,[organizationId+userId],entityType,entityId,createdAt,&idempotencyKey',
        conflicts: '&id,&mutationId,[organizationId+userId],entityType,entityId,detectedAt',
        quarantine: '&id,[organizationId+userId],entityType,entityId,createdAt',
      })
      .upgrade(async (tx) => {
        const rows = (await tx.table('mutations').toArray()) as PendingMutation[];
        for (const row of rows) {
          if (!row.entityType.startsWith('oem:')) {
            await tx
              .table('quarantine')
              .put({ ...row, lastError: 'legacy_mapping_review_required' });
            await tx.table('mutations').delete(row.id);
          }
        }
      });
  }
}

export const offlineDatabase = new SqiOfflineDatabase();

function notifyQueueChanged(): void {
  if (typeof globalThis.dispatchEvent === 'function') {
    globalThis.dispatchEvent(new Event('sqi-offline-queue-changed'));
  }
}

export async function cacheRecord(record: CachedRecord): Promise<void> {
  await offlineDatabase.records.put(
    record.entityType.startsWith('oem:')
      ? {
          ...record,
          payload: await sealPayload(record.userId, record.organizationId, record.payload),
        }
      : record,
  );
}

export async function queueMutation(
  mutation: Omit<PendingMutation, 'id' | 'createdAt' | 'retryCount' | 'idempotencyKey'> & {
    readonly idempotencyKey?: string;
  },
): Promise<PendingMutation> {
  if (mutation.operation === 'delete' || !mutation.entityType.startsWith('oem:draft:'))
    throw new Error('online_only_action');
  const queued: PendingMutation = {
    ...mutation,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    retryCount: 0,
    idempotencyKey: mutation.idempotencyKey ?? crypto.randomUUID(),
  };
  await offlineDatabase.mutations.add({
    ...queued,
    payload: await sealPayload(queued.userId, queued.organizationId, queued.payload),
  });
  notifyQueueChanged();
  return queued;
}

export async function pendingMutationCount(
  organizationId: string,
  userId: string,
): Promise<number> {
  return offlineDatabase.mutations
    .where('[organizationId+userId]')
    .equals([organizationId, userId])
    .count();
}

export async function synchronizeMutationQueue(
  organizationId: string,
  userId: string,
  transport: (mutation: PendingMutation) => Promise<MutationTransportResult>,
): Promise<{ readonly applied: number; readonly conflicted: number; readonly retained: number }> {
  const queued = await offlineDatabase.mutations
    .where('[organizationId+userId]')
    .equals([organizationId, userId])
    .sortBy('createdAt');
  const existingConflicts = await offlineDatabase.conflicts
    .where('[organizationId+userId]')
    .equals([organizationId, userId])
    .toArray();
  const blocked = new Set(existingConflicts.map((c) => c.entityType + ':' + c.entityId));
  let applied = 0;
  let conflicted = 0;
  for (const mutation of queued) {
    if (!mutation.entityType.startsWith('oem:draft:')) continue;
    const aggregate = mutation.entityType + ':' + mutation.entityId;
    if (blocked.has(aggregate)) continue;
    let clear: unknown;
    try {
      clear = await openPayload(userId, organizationId, mutation.payload);
    } catch {
      continue;
    }
    const result = await transport({ ...mutation, payload: clear });
    if (result.state === 'skip') continue;
    if (result.state === 'applied') {
      await offlineDatabase.mutations.delete(mutation.id);
      applied += 1;
      continue;
    }
    if (result.state === 'conflict') {
      const sealedServer = await sealPayload(userId, organizationId, result.serverPayload ?? null);
      await offlineDatabase.transaction(
        'rw',
        offlineDatabase.mutations,
        offlineDatabase.conflicts,
        async () => {
          await offlineDatabase.conflicts.put({
            id: crypto.randomUUID(),
            mutationId: mutation.id,
            organizationId,
            userId,
            entityType: mutation.entityType,
            entityId: mutation.entityId,
            baseVersion: mutation.baseVersion,
            serverVersion: result.serverVersion ?? mutation.baseVersion,
            localPayload: mutation.payload,
            serverPayload: sealedServer,
            detectedAt: new Date().toISOString(),
          });
          await offlineDatabase.mutations.delete(mutation.id);
        },
      );
      blocked.add(aggregate);
      conflicted += 1;
      continue;
    }
    await offlineDatabase.mutations.update(mutation.id, {
      retryCount: mutation.retryCount + 1,
      lastError: result.error ?? 'transient_sync_failure',
    });
    break;
  }
  notifyQueueChanged();
  return {
    applied,
    conflicted,
    retained: await pendingMutationCount(organizationId, userId),
  };
}

export async function resolveConflict(
  conflictId: string,
  resolution: ConflictResolution,
  mergedPayload?: unknown,
): Promise<void> {
  const conflict = await offlineDatabase.conflicts.get(conflictId);
  if (conflict === undefined) throw new Error('conflict_not_found');
  const local = await openPayload(conflict.userId, conflict.organizationId, conflict.localPayload);
  if (resolution === 'keep_server') {
    await offlineDatabase.conflicts.delete(conflictId);
    notifyQueueChanged();
    return;
  }
  if (resolution === 'manual_merge' && mergedPayload === undefined) {
    throw new Error('merged_payload_required');
  }
  await offlineDatabase.transaction(
    'rw',
    offlineDatabase.mutations,
    offlineDatabase.conflicts,
    async () => {
      await queueMutation({
        organizationId: conflict.organizationId,
        userId: conflict.userId,
        entityType: conflict.entityType,
        entityId: conflict.entityId,
        operation: 'update',
        baseVersion: conflict.serverVersion,
        idempotencyKey: crypto.randomUUID(),
        payload: resolution === 'manual_merge' ? mergedPayload : local,
      });
      await offlineDatabase.conflicts.delete(conflictId);
    },
  );
  notifyQueueChanged();
}

export async function purgeOfflineData(): Promise<void> {
  await offlineDatabase.delete();
  if ('caches' in globalThis) {
    for (const name of await caches.keys()) {
      if (name.startsWith('sqi-') || name.startsWith('workbox-')) await caches.delete(name);
    }
  }
  notifyQueueChanged();
}
